import { useCustomReportDataQueryWithParam } from 'hooks';
import { useCallback, useMemo } from 'react';
import { UserEssentials } from 'Types/User.types';
import { ReportFilterOperator } from 'Types/ReportField.types';
import { USER_ESSENTIALS_REPORT_CONFIG } from 'Components/Users/Users.consts';

export const useEnhanceUsersWithManagers = () => {
  const { fetch } = useCustomReportDataQueryWithParam();

  const fetchUsersWithManagers = useCallback(async (): Promise<UserEssentials[]> => {
    const data = await fetch({
      ...USER_ESSENTIALS_REPORT_CONFIG,
      filter: {
        operator: 'And',
        conditions: [
          ...USER_ESSENTIALS_REPORT_CONFIG.filter.conditions,
          {
            field: { id: 'user.manager' },
            operator: ReportFilterOperator.Defined,
            value: null,
            values: null
          }
        ]
      }
    });
    return data.data as unknown as UserEssentials[];
  }, [fetch]);

  const getUsersWithManagers = useCallback(
    async (inputList: UserEssentials[]): Promise<UserEssentials[]> => {
      const managerRelationshipList = await fetchUsersWithManagers();
      const allKnownUserEntries = new Map<number, UserEssentials>();

      for (const entry of managerRelationshipList) {
        if (entry.user.value !== null) {
          allKnownUserEntries.set(entry.user.value, entry);
        }
      }

      for (const entry of inputList) {
        if (entry.user.value !== null) {
          allKnownUserEntries.set(entry.user.value, entry);
        }
      }

      const resultEntriesMap = new Map<number, UserEssentials>(); // Stores UserEntry objects for the final list
      const processingQueue: number[] = [];
      let queueHead = 0;

      for (const entry of inputList) {
        if (entry.user.value !== null) {
          const userId = entry.user.value;
          if (!resultEntriesMap.has(userId)) {
            resultEntriesMap.set(userId, allKnownUserEntries.get(userId)!);
            processingQueue.push(userId);
          }
        }
      }

      while (queueHead < processingQueue.length) {
        const currentUserId = processingQueue[queueHead];
        queueHead += 1; // Dequeue
        const currentUserActualEntry = allKnownUserEntries.get(currentUserId)!;

        if (currentUserActualEntry.manager && currentUserActualEntry.manager.value !== null) {
          const managerId = currentUserActualEntry.manager.value;
          const managerDataFromReference = currentUserActualEntry.manager.data;

          if (!resultEntriesMap.has(managerId)) {
            let managerEntryForList = allKnownUserEntries.get(managerId);

            if (!managerEntryForList) {
              // create a "fake" entry if the manager is not in the known entries
              managerEntryForList = { user: { value: managerId, data: managerDataFromReference }, manager: null };
              allKnownUserEntries.set(managerId, managerEntryForList);
            }
            resultEntriesMap.set(managerId, managerEntryForList);
            processingQueue.push(managerId);
          }
        }
      }

      return Array.from(resultEntriesMap.values());
    },
    [fetchUsersWithManagers]
  );

  return useMemo(() => ({ getUsersWithManagers }), [getUsersWithManagers]);
};
